import { type FC, type ReactNode, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Spacing } from "@snapchat/snap-design-system";
import { ChevronLeftIcon } from "@snapchat/snap-design-system-icons";

import { Visibility } from "../../../gql-generated/types";
import { type LensInfo } from "../../../lens/api/queries";
import { LensDestinationsTooltipContent } from "../../../lens/components/shared/LensDestinationsTooltipContent";
import { LensDistributionRestrictionsModal } from "../../../lens/components/shared/LensDistributionRestrictionsModal";
import { LensStatusWithDetails } from "../../../lens/components/shared/LensStatusWithDetails";
import { LensUpdateAtDate } from "../../../lens/components/shared/LensUpdateAtDate";
import { useLensDestinations } from "../../../lens/hooks";
import { defaultStatusEntry, makeDestinationsText, makeDisplayStatus } from "../../../lens/state";
import { type LensVisibilityConfig } from "../../../lens-visibility/hooks";
import { UserInfoContext } from "../../../shared/api/contexts";
import { Flexbox } from "../../../shared/components";
import { useToggleState } from "../../../shared/hooks";
import { LensNameText, lensDetailsContainer } from "../lens-details/shared/styles";

import { LensDetailsIcon } from "./LensDetailsIcon";
import {
    ButtonWithSpacing,
    DestinationDetails,
    DestinationSubTitle,
    DestinationTitle,
    LensForUpdateContainer,
    LensForUpdateDetails,
    PanelCtaContainer,
    PanelDetailsContainer,
} from "./PublishPanel.styles";

interface PublishPanelProps {
    lensFolderName: string;
    organizationName: string;
    onLensPublish: () => void;
    onLensUpload: () => void;
    isDetailsValid: boolean;
    visibilityConfig: LensVisibilityConfig;
    submitLoading: boolean;
    lensForUpdate?: LensInfo;
    uploadButtonText: ReactNode;
    publishButtonText: ReactNode;
}

export const PublishPanel: FC<PublishPanelProps> = ({
    lensFolderName,
    organizationName,
    onLensPublish,
    onLensUpload,
    isDetailsValid,
    submitLoading,
    lensForUpdate,
    visibilityConfig,
    uploadButtonText,
    publishButtonText,
}) => {
    const navigate = useNavigate();
    const userInfo = useContext(UserInfoContext);

    const canPublishToCommunity = visibilityConfig.communityDistributionOptions?.some(
        (option) => visibilityConfig.disabledOptions?.[option] === undefined,
    );
    const canUploadToLensFolder =
        visibilityConfig.options.includes(Visibility.VisibilityDontPublishToSnapchat) &&
        visibilityConfig.disabledOptions?.[Visibility.VisibilityDontPublishToSnapchat] === undefined;

    const { destinations, loading: destinationsLoading } = useLensDestinations(
        lensForUpdate?.id,
        lensForUpdate?.lensAccountId?.lensSpaceId ?? undefined,
        lensForUpdate?.distributionsList ?? [],
    );

    const [restrictionModalProps, showRestrictionModal] = useToggleState();

    const { name: title, ...lensStatusDisplayMeta } = useMemo(
        () => (lensForUpdate ? makeDisplayStatus(lensForUpdate.status, destinations) : defaultStatusEntry),
        [lensForUpdate, destinations],
    );

    const tagLine = useMemo(
        () =>
            makeDestinationsText(
                destinations,
                userInfo?.data?.getUserProfile.userId ?? "",
                lensForUpdate?.status,
                title,
            ),
        [destinations, userInfo?.data?.getUserProfile.userId, lensForUpdate?.status, title],
    );

    const tooltipContent = lensForUpdate && (
        <LensDestinationsTooltipContent
            loading={destinationsLoading || !!userInfo?.loading}
            destinations={destinations}
            userId={userInfo?.data?.getUserProfile.userId ?? ""}
            distributionRestrictionsList={lensForUpdate.distributionRestrictionsList}
            onLensDestinationHeaderClick={showRestrictionModal}
        />
    );

    return (
        <>
            <Flexbox gap={`${Spacing.MD}px`} alignItems="center" justifyContent="space-between">
                <PanelDetailsContainer data-testid="pp-container">
                    <Button
                        data-testid="pp-back-button"
                        shape="circle"
                        onClick={() => {
                            navigate(-1);
                        }}
                    >
                        <ChevronLeftIcon />
                    </Button>

                    {lensForUpdate ? (
                        <LensForUpdateContainer data-testid="pp-lens-for-update">
                            <LensForUpdateDetails>
                                <div>
                                    <LensDetailsIcon
                                        data-testid="pp-lens-for-update-icon"
                                        iconUrl={lensForUpdate.iconUrl}
                                    />
                                </div>
                                <Flexbox
                                    justifyContent="center"
                                    flexDirection="column"
                                    className={lensDetailsContainer}
                                >
                                    <LensNameText data-testid="pp-lens-for-update-text">
                                        {lensForUpdate.name}
                                    </LensNameText>
                                    <LensUpdateAtDate
                                        dataTestId="pp-lens-for-update-date"
                                        seconds={lensForUpdate.updatedAt?.seconds ?? 0}
                                    />
                                </Flexbox>
                            </LensForUpdateDetails>
                            {tagLine && (
                                <LensStatusWithDetails
                                    loading={!!userInfo?.loading || destinationsLoading}
                                    tagLine={tagLine}
                                    tooltipContent={tooltipContent}
                                    title={title}
                                    width="50%"
                                    hasDistributionRestrictions={lensForUpdate.distributionRestrictionsList.length > 0}
                                    {...lensStatusDisplayMeta}
                                />
                            )}
                        </LensForUpdateContainer>
                    ) : (
                        <DestinationDetails>
                            <DestinationTitle data-testid="pp-title">{lensFolderName}</DestinationTitle>
                            <DestinationSubTitle data-testid="pp-subtitle">{organizationName}</DestinationSubTitle>
                        </DestinationDetails>
                    )}
                </PanelDetailsContainer>
                <PanelCtaContainer>
                    {canUploadToLensFolder && (
                        <ButtonWithSpacing
                            data-testid="upload-folder-button"
                            type={canPublishToCommunity ? "default" : "primary"}
                            isDisabled={!isDetailsValid}
                            onClick={onLensUpload}
                            isPending={submitLoading}
                        >
                            {uploadButtonText}
                        </ButtonWithSpacing>
                    )}
                    {canPublishToCommunity && (
                        <Button
                            type="primary"
                            onClick={onLensPublish}
                            isDisabled={!isDetailsValid}
                            data-testid="upload-publish-button"
                        >
                            {publishButtonText}
                        </Button>
                    )}
                </PanelCtaContainer>
            </Flexbox>
            {lensForUpdate && (
                <LensDistributionRestrictionsModal
                    lensId={lensForUpdate.id}
                    lensName={lensForUpdate.name}
                    modalProps={restrictionModalProps}
                />
            )}
        </>
    );
};
